import { useEffect, useMemo, useRef, useState } from 'react';

import { FaArrowRotateLeft, FaPaperPlane, FaWandMagicSparkles } from 'react-icons/fa6';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useApp } from 'common/AppContext';

/**
 * Flatten the case's steps to the same monolithic newline-separated form
 * the vibe-editor returns, so the working draft is always shaped like the
 * AI's output regardless of whether the source case used custom_steps or
 * custom_steps_separated. Shared-step refs become a single placeholder
 * line ("[SHARED STEP #ID: TITLE]") — the AI is instructed to pass that
 * line through verbatim, since the underlying shared step is read-only
 * from this UI.
 */
function flattenStepsForDraft(testCase) {

    if (!testCase) return '';

    if (Array.isArray(testCase.custom_steps_separated) && testCase.custom_steps_separated.length) {

        const sharedMap = testCase.shared_steps_resolved || {};
        return testCase.custom_steps_separated.map((s, i) => {

            if (s.shared_step_id) {

                const shared = sharedMap[s.shared_step_id];
                const title = shared?.title || `#${s.shared_step_id}`;
                return `${i + 1}. [SHARED STEP #${s.shared_step_id}: ${title}]`;

            }

            const action = (s.content || '').trim();
            const expected = (s.expected || '').trim();
            return `${i + 1}. ${action}${expected ? ` (expected: ${expected})` : ''}`;

        }).join('\n');

    }

    return (testCase.custom_steps || '').trim();

}

function hasSharedSteps(testCase) {

    if (!testCase || !Array.isArray(testCase.custom_steps_separated)) return false;
    return testCase.custom_steps_separated.some(s => s && s.shared_step_id);

}

function buildInitialDraft(testCase) {

    return {
        custom_preconds: testCase?.custom_preconds || '',
        custom_steps: flattenStepsForDraft(testCase),
        title: testCase?.title || ''
    };

}

const SUGGESTIONS = [
    'Make the steps more granular',
    'Add a check for accessibility / keyboard navigation',
    'Rewrite for clarity — shorter sentences',
    'Add edge cases (empty input, special characters, network error)',
    'Tighten the title to under 80 characters'
];

export const VibeEditModal = ({ onApplied, onHide, show, testCase }) => {

    const { api } = useApp();

    const [history, setHistory] = useState([]);
    const [draft, setDraft] = useState(() => buildInitialDraft(testCase));
    const [instruction, setInstruction] = useState('');
    const [thinking, setThinking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const original = useMemo(() => buildInitialDraft(testCase), [testCase]);
    const transcriptRef = useRef(null);

    useEffect(() => {

        if (show) {

            setHistory([]);
            setDraft(buildInitialDraft(testCase));
            setInstruction('');
            setError(null);

        }

    }, [show, testCase]);

    useEffect(() => {

        if (transcriptRef.current)
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;

    }, [history, thinking]);

    const dirty
        = draft.title !== original.title
        || draft.custom_preconds !== original.custom_preconds
        || draft.custom_steps !== original.custom_steps;

    const submitInstruction = async text => {

        const trimmed = (text || '').trim();
        if (!trimmed || thinking || saving) return;

        setThinking(true);
        setError(null);
        setHistory(h => [...h, { role: 'user', text: trimmed }]);
        setInstruction('');

        try {

            const result = await api.post(`/api/testrail/cases/${testCase.id}/vibe-edit`, {
                instruction: trimmed,
                working: draft
            });
            setDraft({
                custom_preconds: result.custom_preconds || '',
                custom_steps: result.custom_steps || '',
                title: result.title || ''
            });
            setHistory(h => [...h, { role: 'assistant', text: result.notes || 'Updated the draft.' }]);

        } catch (e) {

            const msg = e?.err || e?.message || String(e);
            setError(msg);
            setHistory(h => [...h, { error: true, role: 'assistant', text: `Error: ${msg}` }]);

        } finally {

            setThinking(false);

        }

    };

    const reset = () => {

        setDraft(buildInitialDraft(testCase));
        setHistory(h => [...h, { role: 'system', text: 'Draft reset to the original case.' }]);

    };

    const apply = async () => {

        setSaving(true);
        setError(null);

        try {

            const updated = await api.request('PUT', `/api/testrail/cases/${testCase.id}`, {
                custom_preconds: draft.custom_preconds,
                custom_steps: draft.custom_steps,
                title: draft.title
            });
            if (onApplied) onApplied(updated);
            onHide();

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setSaving(false);

        }

    };

    return (
        <Modal centered scrollable onHide={onHide} show={show} size="xl">

            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <FaWandMagicSparkles className="text-primary" />
                    Vibe-edit case
                    <span className="text-muted small">
                        C
                        {testCase?.id}
                    </span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error && (
                    <Alert dismissible onClose={() => setError(null)} variant="danger">
                        {error}
                    </Alert>
                )}

                {hasSharedSteps(testCase) && (
                    <Alert className="small mb-3" variant="info">
                        <strong>
                            Heads up — this case uses shared steps.
                        </strong>
                        {' '}
                        Lines marked
                        {' '}
                        <code>
                            [SHARED STEP #…]
                        </code>
                        {' '}
                        are read-only references; the AI will keep them in place but won&apos;t modify the shared content. Edit the shared step itself in TestRail if you need to change those.
                    </Alert>
                )}

                <div className="row g-3">

                    <div className="col-lg-6 d-flex flex-column" style={{ minHeight: 480 }}>

                        <div className="text-uppercase text-muted small mb-2" style={{ letterSpacing: 0.5 }}>
                            Working draft
                            {dirty && (
                                <Badge bg="warning" className="ms-2 text-dark">
                                    modified
                                </Badge>
                            )}
                        </div>

                        <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">
                                Title
                            </Form.Label>
                            <Form.Control
                                onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                                value={draft.title}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">
                                Preconditions
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                onChange={e => setDraft(d => ({ ...d, custom_preconds: e.target.value }))}
                                rows={3}
                                value={draft.custom_preconds}
                            />
                        </Form.Group>

                        <Form.Group className="mb-0 flex-grow-1 d-flex flex-column">
                            <Form.Label className="small mb-1">
                                Steps
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                className="flex-grow-1"
                                onChange={e => setDraft(d => ({ ...d, custom_steps: e.target.value }))}
                                style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, minHeight: 220 }}
                                value={draft.custom_steps}
                            />
                        </Form.Group>

                    </div>

                    <div className="col-lg-6 d-flex flex-column" style={{ minHeight: 480 }}>

                        <div className="text-uppercase text-muted small mb-2" style={{ letterSpacing: 0.5 }}>
                            Chat
                        </div>

                        <div
                            className="border rounded p-2 flex-grow-1 mb-2"
                            ref={transcriptRef}
                            style={{ background: 'rgba(0,0,0,0.02)', minHeight: 220, overflowY: 'auto' }}
                        >
                            {history.length === 0 && !thinking && (
                                <div className="text-muted small">
                                    <p className="mb-2">
                                        Tell the AI how to improve this case. Try:
                                    </p>
                                    <ul className="mb-0">
                                        {SUGGESTIONS.map(s => (
                                            <li className="mb-1" key={s}>
                                                <Button
                                                    className="text-start p-0"
                                                    onClick={() => submitInstruction(s)}
                                                    size="sm"
                                                    variant="link"
                                                >
                                                    {s}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {history.map((m, i) => (
                                <div
                                    className={`mb-2 small ${m.role === 'user' ? 'text-end' : ''}`}
                                    key={i}
                                >
                                    <div
                                        style={{
                                            background: m.role === 'user'
                                                ? 'var(--bs-primary-bg-subtle, #cfe2ff)'
                                                : m.error
                                                    ? 'var(--bs-danger-bg-subtle, #f8d7da)'
                                                    : 'var(--bs-light, #f8f9fa)',
                                            maxWidth: '90%',
                                            textAlign: 'left',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                        className="d-inline-block px-2 py-1 rounded"
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {thinking && (
                                <div className="small text-muted d-flex align-items-center gap-2">
                                    <Spinner animation="border" size="sm" />
                                    Revising the draft…
                                </div>
                            )}
                        </div>

                        <Form
                            onSubmit={e => {

                                e.preventDefault();
                                submitInstruction(instruction);

                            }}
                        >
                            <Form.Group>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        onKeyDown={e => {

                                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {

                                                e.preventDefault();
                                                submitInstruction(instruction);

                                            }

                                        }}
                                        disabled={thinking || saving}
                                        onChange={e => setInstruction(e.target.value)}
                                        placeholder="e.g. Add a step that verifies the confirmation email"
                                        value={instruction}
                                    />
                                    <Button
                                        disabled={thinking || saving || !instruction.trim()}
                                        title="Send (⌘/Ctrl+Enter)"
                                        type="submit"
                                        variant="primary"
                                    >
                                        <FaPaperPlane />
                                    </Button>
                                </div>
                            </Form.Group>
                        </Form>

                    </div>

                </div>

            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button
                    disabled={!dirty || thinking || saving}
                    onClick={reset}
                    size="sm"
                    variant="outline-secondary"
                >
                    <FaArrowRotateLeft className="me-1" />
                    Reset to original
                </Button>
                <div className="d-flex gap-2">
                    <Button disabled={saving} onClick={onHide} variant="outline-secondary">
                        Cancel
                    </Button>
                    <Button
                        disabled={!dirty || saving || thinking || !draft.title.trim()}
                        onClick={apply}
                        variant="success"
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" as="span" className="me-2" size="sm" />
                                Saving…
                            </>
                        ) : 'Apply to TestRail'}
                    </Button>
                </div>
            </Modal.Footer>

        </Modal>
    );

};
